import { type PermissionedResource } from '@lib/permission';
import SPInspection from '@lib/spproject/SPInspection';
import { type Inspection } from '@lib/spproject/types';
import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

export interface InspectionAuthor {
  _id: string;
  name: string;
  username: string;
}

export interface IInspection extends PermissionedResource {
  _id: string;
  /** Author of the inspection; drives the self-scope answer in `myPermissions`. */
  userId?: string;
  /**
   * The author's public identity, for display. A separate field from `userId` rather than a
   * populated one, because the id form is what the backend answers the self scope against.
   * Null when the referenced user no longer exists.
   */
  author?: InspectionAuthor | null;
  info: Inspection.Info;
  notes?: Inspection.PointOfInterest[];
  pointOfInterests?: Inspection.PointOfInterest[];
  /** Inspection-level attachments, normalized from the legacy `documents` key on receipt. */
  resources?: Inspection.Document[];
  // [key: string]: unknown;
}

/**
 * Field projection asked of the API. `notes`/`documents` are the legacy names of
 * `pointOfInterests`/`resources` and are still what old inspections are stored under,
 * so both names have to be requested to get either back.
 */
export type InspectionFields = ('notes' | 'pointOfInterests' | 'documents' | 'resources')[];

export const inspectionApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInspections: builder.query<IInspection[], { projectId: string; include?: InspectionFields }>({
      query: ({ projectId, include }) =>
        `/v1/projects/${projectId}/inspections${include ? `?include=${include.join(',')}` : ''}`,
      providesTags: (result, _error, { projectId }) => [
        { type: 'Inspection', id: 'LIST' },
        ...(result?.map(({ info }) => ({ type: 'Inspection' as const, id: info.id })) || []),
        { type: 'Project', id: projectId },
      ],
      transformResponse: (inspections: IInspection[]) => inspections.map(inspection => {
        SPInspection.transformInspectionForCompatibility(inspection as unknown as Inspection.Inspection);
        return inspection;
      }),
      onQueryStarted: handleApiResult(),
    }),
    getInspection: builder
      .query<IInspection, { projectId: string; inspectionId: string; include?: InspectionFields }>({
        query: ({ projectId, inspectionId, include }) =>
          `/v1/projects/${projectId}/inspections/${inspectionId}${include ? `?include=${include.join(',')}` : ''}`,
        providesTags: (_result, _error, { inspectionId }) => [
          { type: 'Inspection', id: inspectionId },
        ],
        transformResponse: (inspection: IInspection) => {
          SPInspection.transformInspectionForCompatibility(inspection as unknown as Inspection.Inspection);
          return inspection;
        },
        onQueryStarted: handleApiResult(),
      }),
    getInspectionFile: builder.query<string, { projectId: string; inspectionId: string }>({
      query: ({ projectId, inspectionId }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}/file`,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
      onQueryStarted: handleApiResult(),
    }),
    getInspectionReport: builder.query<string, { projectId: string; inspectionId: string; locale: string; }>({
      query: ({ projectId, inspectionId, locale }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}/reports/pdf?locale=${locale ?? ''}`,
        responseHandler: async (response) => response.ok ? response.blob() : response.json(),
      }),
      transformResponse: (blob: Blob) => (URL || webkitURL).createObjectURL(blob),
      onQueryStarted: handleApiResult(),
    }),
    addInspection: builder.mutation<void, { projectId: string; formData: FormData }>({
      query: ({ projectId, formData }) => ({
        url: `/v1/projects/${projectId}/inspections`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'Inspection', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_Inspection_Add_Successful'),
    }),
    updateInspection: builder.mutation<void, { projectId: string; inspectionId: string; formData: FormData }>({
      query: ({ projectId, inspectionId, formData }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'Inspection', id: inspectionId },
      ],
      onQueryStarted: handleApiResult('Api_Inspection_Update_Successful'),
    }),
    deleteInspection: builder.mutation<void, { projectId: string; inspectionId: string }>({
      query: ({ projectId, inspectionId }) => ({
        url: `/v1/projects/${projectId}/inspections/${inspectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { inspectionId }) => [
        { type: 'Inspection', id: inspectionId },
      ],
      onQueryStarted: handleApiResult('Api_Inspection_Delete_Successful'),
    }),
  }),
});

export const {
  useGetInspectionsQuery,
  useGetInspectionQuery,
  useLazyGetInspectionsQuery,
  useLazyGetInspectionFileQuery,
  useLazyGetInspectionReportQuery,
  useAddInspectionMutation,
  useUpdateInspectionMutation,
  useDeleteInspectionMutation,
} = inspectionApiSlice;
